import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Building2, 
  Calendar, 
  DollarSign, 
  Mail, 
  Phone, 
  MapPin,
  FileText,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Palette,
  Type,
  MoreHorizontal,
  ChevronDown,
  Globe,
  Search,
  Download,
  Loader2,
  X
} from 'lucide-react';
import { Invoice } from '../../types/invoice';
import { CompanySettings } from '../../hooks/useCompanySettings';
import { isMinimalInvoiceEmailValid } from '../../common/utils/validation';
import ColorPicker from '../ColorPicker';

// Helper function to format addresses in two-line format
// Format: "Address, State" on first line, "City, Country, Postcode" on second line
const formatAddressTwoLine = (address: {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}): string => {
  const lines: string[] = [];
  
  // First line: Address, State
  const firstLineParts = [];
  if (address.street?.trim()) {
    firstLineParts.push(address.street.trim());
  }
  if (address.state?.trim()) {
    firstLineParts.push(address.state.trim());
  }
  if (firstLineParts.length > 0) {
    lines.push(firstLineParts.join(', '));
  }
  
  // Second line: City, Country, Postcode
  const secondLineParts = [];
  if (address.city?.trim()) {
    secondLineParts.push(address.city.trim());
  }
  if (address.country?.trim()) {
    secondLineParts.push(address.country.trim());
  }
  if (address.zipCode?.trim()) {
    secondLineParts.push(address.zipCode.trim());
  }
  if (secondLineParts.length > 0) {
    lines.push(secondLineParts.join(', '));
  }
  
  return lines.join('\n');
};

// Helper function to format notes with smart line breaks
const formatNotesWithLineBreaks = (notes: string): string => {
  const maxCharsPerLine = 120;
  
  if (notes.length <= maxCharsPerLine) {
    return notes;
  }
  
  const lines: string[] = [];
  let currentLine = '';
  const words = notes.split(' ');
  
  for (const word of words) {
    const potentialLine = currentLine + (currentLine ? ' ' : '') + word;
    
    if (potentialLine.length <= maxCharsPerLine) {
      currentLine = potentialLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }
  
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines.join('\n');
};

// Helper function to validate and format URLs
const formatWebsiteUrl = (url: string): string => {
  if (!url) return '';
  
  // Remove any whitespace
  url = url.trim();
  
  // If it doesn't start with http:// or https://, add https://
  if (!url.match(/^https?:\/\//i)) {
    url = 'https://' + url;
  }
  
  return url;
};

// Helper function to validate if URL is properly formatted
const isValidUrl = (url: string): boolean => {
  if (!url) return false;
  
  try {
    const formattedUrl = formatWebsiteUrl(url);
    new URL(formattedUrl);
    return true;
  } catch {
    return false;
  }
};

interface InvoiceLivePreviewProps {
  invoice: Invoice;
  companySettings: CompanySettings;
  formattedAddress?: string;
  customization?: {
    primaryColor: string;
    fontFamily: string;
    headerBackgroundColor: string;
    logoPosition: string;
    showLogo: boolean;
    showTaxNumber: boolean;
    showPaymentTerms: boolean;
    showNotes: boolean;
    showItemCodes: boolean;
    showCompanyAddress: boolean;
    showWebsite: boolean;
    showContactInfo: boolean;
  };
  uploadedLogo?: string | null;
  template?: string;
  availableTemplates?: any;
  templateConfig?: any;
  onTemplateChange?: (direction: 'prev' | 'next') => void;
  customizationStyles?: any;
  updateCustomStyle?: (key: string, value: any) => void;
  showDisplayOptionsDropdown?: boolean;
  setShowDisplayOptionsDropdown?: (show: boolean) => void;
  isDark?: boolean;
  // Currency props
  currency?: string;
  onCurrencyChange?: (currency: string) => void;
  showCurrencyDropdown?: boolean;
  setShowCurrencyDropdown?: (show: boolean) => void;
  getCurrencySymbol?: (currency: string) => string;
  // Download props
  onDownloadPDF?: () => void;
  isGeneratingPDF?: boolean;
  // Form data for validation
  formData?: any;
  validationErrors?: any;
  setValidationErrors?: (errors: any) => void;
}

export const InvoiceLivePreview: React.FC<InvoiceLivePreviewProps> = ({
  invoice,
  companySettings,
  formattedAddress,
  customization,
  uploadedLogo,
  template = 'modern',
  availableTemplates = {},
  templateConfig,
  onTemplateChange,
  customizationStyles,
  updateCustomStyle,
  showDisplayOptionsDropdown,
  setShowDisplayOptionsDropdown,
  isDark,
  // Currency props
  currency,
  onCurrencyChange,
  showCurrencyDropdown,
  setShowCurrencyDropdown,
  getCurrencySymbol,
  // Download props
  onDownloadPDF,
  isGeneratingPDF,
  // Form data for validation
  formData,
  validationErrors,
  setValidationErrors
}) => {
  const previewMode = 'desktop';
  const [logoError, setLogoError] = useState(false);
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [currencySearchQuery, setCurrencySearchQuery] = useState('');
  const [previousTemplate, setPreviousTemplate] = useState(template);
  const [animationDirection, setAnimationDirection] = useState<'left' | 'right'>('right');
  const [animationKey, setAnimationKey] = useState(0);
  const lastDirectionRef = useRef<'left' | 'right'>('right');

  // Validation function to check if all required fields are filled
  const isFormValid = () => {
    if (!formData) return true; // Allow download if formData is not provided
    
    const requiredFields = [
      'businessName', 'businessEmail', 'businessAddress', 'businessCity', 'businessState', 'businessZipCode',
      'customerName', 'customerEmail', 'customerAddress', 'customerCity', 'customerState', 'customerZipCode',
      'dueDate'
    ];
    
    // Helper function to validate email format
    const isValidEmail = (email: string) => {
      return isMinimalInvoiceEmailValid(email);
    };
    
    // Helper function to validate website URL
    const isValidWebsite = (website: string) => {
      if (!website || website.trim() === '') return true; // Website is optional
      try {
        const url = website.startsWith('http') ? website : `https://${website}`;
        new URL(url);
        return true;
      } catch {
        return false;
      }
    };
    
    // Check required text fields - now includes email validation
    const hasRequiredFields = requiredFields.every(field => {
      const value = formData[field];
      if (!value || value.toString().trim() === '') return false;
      
      // Special validation for email fields
      if (field === 'businessEmail' || field === 'customerEmail') {
        return isValidEmail(value);
      }
      
      return true;
    });
    
    // Additional validation for website field if it exists and has content
    const hasValidWebsite = !formData.businessWebsite || formData.businessWebsite.trim() === '' || isValidWebsite(formData.businessWebsite);
    
    // Check line items
    const hasValidLineItems = formData.lineItems && 
      formData.lineItems.length > 0 &&
      formData.lineItems.every((item: any) => 
        item.description && item.description.trim() &&
        item.quantity > 0 && item.rate > 0
      );
    
    return hasRequiredFields && hasValidWebsite && hasValidLineItems;
  };
  
  // State to track form validation to trigger re-renders
  const [formValidationState, setFormValidationState] = useState(isFormValid());

  // Update form validation state whenever formData changes
  useEffect(() => {
    setFormValidationState(isFormValid());
  }, [formData]);

  // Update button styling when theme changes
  useEffect(() => {
    const button = document.querySelector('[data-download-button] button');
    if (button) {
      if (!formValidationState) {
        // Reset to default invalid state styling
        if (isDark) {
          (button as HTMLElement).style.background = '#1f2937';
          (button as HTMLElement).style.borderColor = '#374151';
          (button as HTMLElement).style.boxShadow = 'none';
          (button as HTMLElement).style.opacity = '0.7';
        } else {
          (button as HTMLElement).style.background = '#f3f4f6';
          (button as HTMLElement).style.borderColor = '#d1d5db';
          (button as HTMLElement).style.boxShadow = 'none';
        }
      } else {
        // Set valid state styling
        if (isDark) {
          (button as HTMLElement).style.background = 'rgba(167, 238, 67, 0.12)';
          (button as HTMLElement).style.borderColor = 'rgba(237, 255, 0, 0.5)';
          (button as HTMLElement).style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)';
          (button as HTMLElement).style.opacity = '1';
        } else {
          (button as HTMLElement).style.background = '#ffffff';
          (button as HTMLElement).style.borderColor = 'rgba(237, 255, 0, 0.5)';
          (button as HTMLElement).style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
          (button as HTMLElement).style.opacity = '1';
        }
      }
    }
  }, [isDark, formValidationState]);

  // Track template changes for animation
  useEffect(() => {
    if (template !== previousTemplate) {
      setPreviousTemplate(template);
      // Use the last direction that was set before the template change
      setAnimationDirection(lastDirectionRef.current);
    }
  }, [template, previousTemplate]);

  // Filter currencies based on search query
  const getFilteredCurrencies = () => {
    const currencies = getSupportedCurrencies();
    if (!currencySearchQuery.trim()) {
      return currencies;
    }
    return currencies.filter(curr => 
      curr.code.toLowerCase().includes(currencySearchQuery.toLowerCase()) ||
      curr.symbol.toLowerCase().includes(currencySearchQuery.toLowerCase())
    );
  };

  // Currency support function
  const getSupportedCurrencies = () => [
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
    { code: 'GBP', symbol: '£' },
    { code: 'CAD', symbol: 'C$' },
    { code: 'AUD', symbol: 'A$' },
    { code: 'JPY', symbol: '¥' },
    { code: 'CHF', symbol: 'CHF' },
    { code: 'CNY', symbol: '¥' },
    { code: 'INR', symbol: '₹' },
    { code: 'BRL', symbol: 'R$' },
    { code: 'MXN', symbol: '$' },
    { code: 'SGD', symbol: 'S$' },
    { code: 'HKD', symbol: 'HK$' },
    { code: 'NZD', symbol: 'NZ$' },
    { code: 'SEK', symbol: 'kr' },
    { code: 'NOK', symbol: 'kr' },
    { code: 'DKK', symbol: 'kr' },
    { code: 'PLN', symbol: 'zł' },
    { code: 'CZK', symbol: 'Kč' },
    { code: 'HUF', symbol: 'Ft' },
    { code: 'RUB', symbol: '₽' },
    { code: 'ZAR', symbol: 'R' },
    { code: 'KRW', symbol: '₩' },
    { code: 'THB', symbol: '฿' },
    { code: 'MYR', symbol: 'RM' },
    { code: 'PHP', symbol: '₱' },
    { code: 'IDR', symbol: 'Rp' },
    { code: 'VND', symbol: '₫' },
    { code: 'TRY', symbol: '₺' },
    { code: 'ILS', symbol: '₪' },
    { code: 'AED', symbol: 'د.إ' },
    { code: 'SAR', symbol: '﷼' },
    { code: 'QAR', symbol: '﷼' },
    { code: 'KWD', symbol: 'د.ك' },
    { code: 'BHD', symbol: 'د.ب' },
    { code: 'OMR', symbol: '﷼' },
    { code: 'JOD', symbol: 'د.ا' },
    { code: 'LBP', symbol: 'ل.ل' },
    { code: 'EGP', symbol: '£' },
    { code: 'MAD', symbol: 'د.م.' },
    { code: 'TND', symbol: 'د.ت' },
    { code: 'DZD', symbol: 'د.ج' },
    { code: 'NGN', symbol: '₦' },
    { code: 'GHS', symbol: '₵' },
    { code: 'KES', symbol: 'KSh' },
    { code: 'UGX', symbol: 'USh' },
    { code: 'TZS', symbol: 'TSh' },
    { code: 'ETB', symbol: 'Br' },
    { code: 'MUR', symbol: '₨' },
    { code: 'SCR', symbol: '₨' },
    { code: 'MVR', symbol: 'ރ' },
    { code: 'LKR', symbol: '₨' },
    { code: 'BDT', symbol: '৳' },
    { code: 'PKR', symbol: '₨' },
    { code: 'AFN', symbol: '؋' },
    { code: 'NPR', symbol: '₨' },
    { code: 'BTN', symbol: 'Nu.' },
    { code: 'MMK', symbol: 'K' },
    { code: 'LAK', symbol: '₭' },
    { code: 'KHR', symbol: '៛' },
    { code: 'MNT', symbol: '₮' },
    { code: 'UZS', symbol: 'лв' },
    { code: 'KZT', symbol: '₸' },
    { code: 'KGS', symbol: 'лв' },
    { code: 'TJS', symbol: 'SM' },
    { code: 'AMD', symbol: '֏' },
    { code: 'AZN', symbol: '₼' },
    { code: 'GEL', symbol: '₾' },
    { code: 'MDL', symbol: 'L' },
    { code: 'UAH', symbol: '₴' },
    { code: 'BYN', symbol: 'Br' },
    { code: 'BGN', symbol: 'лв' },
    { code: 'RON', symbol: 'lei' },
    { code: 'HRK', symbol: 'kn' },
    { code: 'RSD', symbol: 'дин' },
    { code: 'BAM', symbol: 'КМ' },
    { code: 'MKD', symbol: 'ден' },
    { code: 'ALL', symbol: 'L' },
    { code: 'ISK', symbol: 'kr' },
    { code: 'FJD', symbol: 'FJ$' },
    { code: 'PGK', symbol: 'K' },
    { code: 'SBD', symbol: 'SI$' },
    { code: 'VUV', symbol: 'Vt' },
    { code: 'WST', symbol: 'WS$' },
    { code: 'TOP', symbol: 'T$' },
    { code: 'XPF', symbol: '₣' }
  ];

  // Clear search query when dropdown closes
  useEffect(() => {
    if (!showCurrencyDropdown) {
      setCurrencySearchQuery('');
    }
  }, [showCurrencyDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      if (showDisplayOptionsDropdown && !target.closest('.display-options-dropdown')) {
        setShowDisplayOptionsDropdown?.(false);
      }
      
      if (showFontDropdown && !target.closest('.font-dropdown')) {
        setShowFontDropdown(false);
      }
    };

    if (showDisplayOptionsDropdown || showFontDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDisplayOptionsDropdown, setShowDisplayOptionsDropdown, showFontDropdown]);

  // Reset logo error when logo URL changes
  useEffect(() => {
    setLogoError(false);
  }, [uploadedLogo, companySettings.logo]);

  const formatCurrency = (amount: number) => {
    // Preview requirement: always show EXACTLY 2 decimals
    const num = Number(amount) || 0;
    // Force exactly 2 decimal places by using integer math
    const cents = Math.round(num * 100);
    const dollars = Math.floor(cents / 100);
    const remainingCents = Math.abs(cents % 100);
    const decimalPart = remainingCents.toString().padStart(2, '0');
    const numericStr = `${dollars}.${decimalPart}`;
    
    // Get currency symbol
    const symbol = (getCurrencySymbol && invoice.currency) 
      ? getCurrencySymbol(invoice.currency) 
      : '$';
    
    return `${symbol}${numericStr}`;
  };

  const getTextColorForBackground = (backgroundColor: string, isSecondary = false) => {
    if (!backgroundColor) return isSecondary ? '#6B7280' : '#111827';

    // Remove # if present and convert to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate brightness (0-255)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Return black text for light backgrounds, white for dark backgrounds
    if (isSecondary) {
      return brightness > 170 ? '#6B7280' : '#D1D5DB';
    }
    return brightness > 170 ? '#111827' : '#FFFFFF';
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Apply customization styles
  const getCustomStyles = () => {
    if (!customization) return {};

    const fontFamily = customization.fontFamily
      ? `"${customization.fontFamily}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      : '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    
    return {
      fontFamily: fontFamily,
      primaryColor: customization.primaryColor,
      headerBackgroundColor: customization.headerBackgroundColor || '#FFFFFF',
    };
  };

  const customStyles = getCustomStyles();

  // Get template-specific styles and layout
  const getTemplateStyles = () => {
    const baseStyles: Record<string, {
      headerBg: string;
      headerText: string;
      accentColor: string;
      borderRadius: string;
      shadow: string;
      layout: string;
    }> = {
      modern: {
        headerBg: 'bg-gradient-to-br from-[#edff00] to-[#d4e600]',
        headerText: 'text-black',
        accentColor: '#edff00',
        borderRadius: 'rounded-xl',
        shadow: 'shadow-2xl',
        layout: 'modern'
      },
      clean: {
        headerBg: 'bg-white',
        headerText: 'text-gray-900',
        accentColor: '#3B82F6',
        borderRadius: 'rounded-none',
        shadow: 'shadow-sm',
        layout: 'clean'
      }
    };

    const templateStyle = baseStyles[template] || baseStyles.modern;
    
    // Apply custom colors if provided
    if (customization) {
      // Determine text color based on background brightness
      const getTextColorForBackground = (backgroundColor: string) => {
        if (!backgroundColor) return templateStyle.headerText;
        
        // Remove # if present and convert to RGB
        const hex = backgroundColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        // Calculate brightness (0-255)
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        
        // Return black text for light backgrounds, white for dark backgrounds
        return brightness > 170 ? 'text-black' : 'text-white';
      };

      return {
        ...templateStyle,
        accentColor: customization.primaryColor || templateStyle.accentColor,
        headerBg: customization.headerBackgroundColor 
          ? `bg-[${customization.headerBackgroundColor}]` 
          : templateStyle.headerBg,
        headerText: customization.headerBackgroundColor 
          ? getTextColorForBackground(customization.headerBackgroundColor)
          : templateStyle.headerText
      };
    }
    
    return templateStyle;
  };

  const templateStyles = getTemplateStyles();

  // Render different layouts based on template
  const renderInvoiceLayout = () => {
    switch (templateStyles.layout) {
      case 'modern':
        return renderModernLayout();
      case 'clean':
        return renderCleanProfessionalLayout();
      default:
        return renderModernLayout();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-neutral-600 text-neutral-200';
      case 'sent': return 'bg-blue-600 text-blue-100';
      case 'paid': return 'bg-green-600 text-green-100';
      case 'overdue': return 'bg-red-600 text-red-100';
      default: return 'bg-neutral-600 text-neutral-200';
    }
  };

  // Layout rendering functions
  const renderModernLayout = () => (
    <div className="bg-white px-4 py-2 pb-6" style={{ minHeight: 'calc(100vh - 1.5rem)' }}>
          {/* Modern Header - Side by side layout */}
          <div className={`p-6 border-l-4 rounded-lg`} style={{ 
            backgroundColor: customization?.headerBackgroundColor || '#f3f4f6',
            borderLeftColor: templateStyles.accentColor 
          }}>
        <div className="flex justify-between items-start">
          {/* Left: Company Info */}
          <div className={`${customization?.showLogo === false ? 'text-left' : 'flex-1'}`}>
            {customization?.showLogo !== false && (uploadedLogo || companySettings.logo) && !logoError ? (
              <img
                src={uploadedLogo || companySettings.logo}
                alt={companySettings.companyName}
                className="h-12 w-auto mb-3 rounded-md"
                onError={() => setLogoError(true)}
              />
            ) : customization?.showLogo !== false ? (
              <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            ) : null}
            <h1 className={`text-lg font-semibold mb-2`} style={{ 
              color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6'),
              fontFamily: customStyles.fontFamily
            }}>
              {companySettings.companyName}
            </h1>
            {customization?.showCompanyAddress !== false && formattedAddress && (
              <div className={`text-sm whitespace-pre-line mb-2`} style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                fontFamily: customStyles.fontFamily
              }}>
                {formattedAddress}
              </div>
            )}
            {companySettings.phone && (
              <div className={`text-sm mt-1`} style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                fontFamily: customStyles.fontFamily
              }}>
                <svg style={{width: '12px', height: '12px', display: 'inline-block', marginRight: '4px', verticalAlign: 'middle'}} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                </svg>
                {companySettings.phone}
              </div>
            )}
            {companySettings.email && (
              <div className={`text-sm mt-1`} style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                fontFamily: customStyles.fontFamily
              }}>
                <svg style={{width: '12px', height: '12px', display: 'inline-block', marginRight: '4px', verticalAlign: 'middle'}} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                </svg>
                {companySettings.email}
              </div>
            )}
            {companySettings.website && customization?.showWebsite !== false && (
              <div className={`text-sm mt-1`} style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                fontFamily: customStyles.fontFamily
              }}>
                <svg style={{width: '12px', height: '12px', display: 'inline-block', marginRight: '4px', verticalAlign: 'middle'}} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z"></path>
                </svg>
                {isValidUrl(companySettings.website) ? (
                  <a 
                    href={formatWebsiteUrl(companySettings.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ 
                      color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true)
                    }}
                  >
                    {companySettings.website}
                  </a>
                ) : (
                  <span>{companySettings.website}</span>
                )}
              </div>
            )}
            {companySettings.taxId && customization?.showTaxNumber !== false && (
              <div className={`text-sm mt-1`} style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                fontFamily: customStyles.fontFamily
              }}>
                <span className="font-medium">Tax ID:</span> {companySettings.taxId}
              </div>
            )}
          </div>
          
          {/* Right: Invoice Info */}
          <div className="text-right">
            <div className={`text-xl font-bold mb-2`} style={{ 
              color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6'),
              fontFamily: customStyles.fontFamily
            }}>
              INVOICE
            </div>
            <div className={`text-lg`} style={{ 
              color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
              fontFamily: customStyles.fontFamily
            }}>
              {invoice.invoiceNumber || 'Draft'}
            </div>
          </div>
        </div>
      </div>

      {/* Modern Content */}
      <div className="p-6">
        {/* Bill To & Invoice Details */}
        <div className="grid grid-cols-2 gap-6 mb-4">
          <div>
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Bill To</h3>
            <div className="text-gray-900">
              <div className="font-semibold text-xl mb-2">{invoice.client?.name || 'No client name'}</div>
              {invoice.client?.email && <div className="text-sm text-gray-600 mb-2">{invoice.client.email}</div>}
              {invoice.client?.address && (
                <div className="text-sm text-gray-600 whitespace-pre-line">
                  {formatAddressTwoLine(invoice.client.address)}
                </div>
              )}
            </div>
          </div>
          
          <div className="text-right">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Invoice Details</h3>
            <div className="space-y-2">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Issue Date</div>
                <div className="text-base font-medium text-gray-900">{formatDate(invoice.issueDate)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Due Date</div>
                <div className="text-base font-medium text-gray-900">{formatDate(invoice.dueDate)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-4">
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 font-medium text-gray-700 text-xs uppercase tracking-wide" style={{ width: '60%' }}>Description</th>
                <th className="text-right py-3 font-medium text-gray-700 w-20 text-xs uppercase tracking-wide">Qty</th>
                <th className="text-right py-3 font-medium text-gray-700 w-24 text-xs uppercase tracking-wide">Rate</th>
                <th className="text-right py-3 font-medium text-gray-700 w-24 text-xs uppercase tracking-wide">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={item.id} className="border-b border-gray-100">
                  <td className="py-3">
                    <div className="font-semibold text-gray-900 text-lg">
                      {item.description || <span className="italic text-gray-400">No description</span>}
                    </div>
                    {item.notes && (
                      <div className="text-sm text-gray-500 mt-1 italic" style={{ 
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {formatNotesWithLineBreaks(item.notes)}
                      </div>
                    )}
                  </td>
                  <td className="py-3 text-right text-gray-700 text-lg">{item.quantity}</td>
                  <td className="py-3 text-right text-gray-700 text-lg">{formatCurrency(item.rate)}</td>
                  <td className="py-3 text-right font-medium text-gray-900 text-base">
                    {formatCurrency(item.amount)}
                    {(item.taxRate ?? 0) > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Tax: {item.taxRate}%
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-80">
            <div className="space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-semibold">{formatCurrency(invoice.totals.subtotal)}</span>
              </div>
              {invoice.totals.taxAmount > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-semibold">{formatCurrency(invoice.totals.taxAmount)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 border-t-2 border-gray-200">
                <span className="text-lg font-semibold text-gray-900">Total:</span>
                <div className="bg-gray-100 border-l-4 px-4 py-2 rounded" style={{ borderLeftColor: templateStyles.accentColor }}>
                  <span className="text-xl font-bold text-black">{formatCurrency(invoice.totals.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && customization?.showNotes !== false && (
          <div className="mt-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: customStyles.fontFamily }}>Notes</h3>
            <div className="bg-gray-50 p-4 rounded-lg text-gray-700" style={{ fontFamily: customStyles.fontFamily }}>
              {invoice.notes.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Details */}
        {invoice.paymentDetails && (invoice.paymentDetails.iban || invoice.paymentDetails.bic || invoice.paymentDetails.sortCode || invoice.paymentDetails.accountNumber || invoice.paymentDetails.paymentLink) && (
          <div className="mt-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-600 mb-4 text-left" style={{ fontFamily: customStyles.fontFamily }}>
              Transfer the amount to the account below. Please include invoice number on your reference.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {invoice.paymentDetails.iban && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">IBAN</div>
                  <div className="font-semibold text-gray-900 break-all" style={{ fontFamily: customStyles.fontFamily }}>{invoice.paymentDetails.iban}</div>
                </div>
              )}
              {invoice.paymentDetails.bic && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">BIC</div>
                  <div className="font-semibold text-gray-900 break-all" style={{ fontFamily: customStyles.fontFamily }}>{invoice.paymentDetails.bic}</div>
                </div>
              )}
              {invoice.paymentDetails.sortCode && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Sort Code</div>
                  <div className="font-semibold text-gray-900 break-all" style={{ fontFamily: customStyles.fontFamily }}>{invoice.paymentDetails.sortCode}</div>
                </div>
              )}
              {invoice.paymentDetails.accountNumber && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Account Number</div>
                  <div className="font-semibold text-gray-900 break-all" style={{ fontFamily: customStyles.fontFamily }}>{invoice.paymentDetails.accountNumber}</div>
                </div>
              )}
              {invoice.paymentDetails.paymentLink && (
                <div className="md:col-span-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Payment Link</div>
                  <a 
                    href={invoice.paymentDetails.paymentLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold break-all"
                    style={{ fontFamily: customStyles.fontFamily }}
                  >
                    {invoice.paymentDetails.paymentLink}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );


  const renderProfessionalLayout_UNUSED = () => (
    <div className="bg-white">
          {/* Professional Header */}
          <div className={`p-8 border-l-4`} style={{ 
            backgroundColor: customization?.headerBackgroundColor || '#f3f4f6',
            borderLeftColor: templateStyles.accentColor 
          }}>
        <div className="flex justify-between items-center">
          <div className={`flex items-center ${customization?.showLogo === false ? 'items-start' : ''}`}>
            {customization?.showLogo !== false && (uploadedLogo || companySettings.logo) && !logoError ? (
              <img
                src={uploadedLogo || companySettings.logo}
                alt={companySettings.companyName}
                className="h-20 w-auto"
                onError={() => setLogoError(true)}
              />
            ) : customization?.showLogo !== false ? (
              <div className="h-20 w-20 bg-white/20 rounded-lg flex items-center justify-center">
                <Building2 className="w-10 h-10 text-white" />
              </div>
            ) : null}
            <div className={customization?.showLogo === false ? 'ml-0' : 'ml-6'}>
              <h1 className={`text-lg font-semibold ${customization?.showLogo === false ? 'mb-4' : ''}`} style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6'),
                fontFamily: customStyles.fontFamily
              }}>
                {companySettings.companyName}
              </h1>
              {customization?.showCompanyAddress !== false && formattedAddress && (
                <div className={`text-sm whitespace-pre-line ${customization?.showLogo === false ? 'mb-4' : ''}`} style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                  fontFamily: customStyles.fontFamily
                }}>
                  {formattedAddress}
                </div>
              )}
            </div>
          </div>
          
          <div className="text-right">
            <div className={`text-xl font-bold mb-2`} style={{ 
              color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6'),
              fontFamily: customStyles.fontFamily
            }}>
              INVOICE
            </div>
            <div className={`text-sm`} style={{ 
              color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
              fontFamily: customStyles.fontFamily
            }}>
              Invoice {invoice.invoiceNumber || 'Draft'}
            </div>
          </div>
        </div>
      </div>

      {/* Professional Content */}
      <div className="p-6">
        {/* Bill To & Invoice Details */}
        <div className="grid grid-cols-2 gap-8 mb-4">
          <div>
            <h3 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-3" style={{ fontFamily: customStyles.fontFamily }}>Bill To</h3>
            <div className="text-gray-700">
              <div className="font-semibold text-lg" style={{ fontFamily: customStyles.fontFamily }}>{invoice.client?.name || 'No client name'}</div>
              {invoice.client?.email && <div className="text-sm" style={{ fontFamily: customStyles.fontFamily }}>{invoice.client.email}</div>}
              {invoice.client?.address && (
                <div className="mt-2 text-sm whitespace-pre-line" style={{ fontFamily: customStyles.fontFamily }}>
                  {formatAddressTwoLine(invoice.client.address)}
                </div>
              )}
            </div>
          </div>
          
          <div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-600 font-medium">Issue Date</div>
                  <div className="font-semibold">{formatDate(invoice.issueDate)}</div>
                </div>
                <div>
                  <div className="text-gray-600 font-medium">Due Date</div>
                  <div className="font-semibold">{formatDate(invoice.dueDate)}</div>
                </div>
              </div>
            </div>
        </div>
      </div>

        {/* Items Table */}
        <div className="mb-4">
          <table className="w-full table-fixed">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-4 px-4 font-medium text-gray-700 text-xs uppercase tracking-wide" style={{ width: '60%' }}>Description</th>
                <th className="text-center py-4 px-4 font-medium text-gray-700 text-xs uppercase tracking-wide w-20">Qty</th>
                <th className="text-right py-4 px-4 font-medium text-gray-700 text-xs uppercase tracking-wide w-24">Rate</th>
                <th className="text-right py-4 px-4 font-medium text-gray-700 text-xs uppercase tracking-wide w-24">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={item.id} className="border-b border-gray-100">
                  <td className="py-4 px-4">
                    <div className="font-semibold text-gray-900 text-base">
                      {item.description || <span className="italic text-gray-400">No description</span>}
                    </div>
                    {item.notes && (
                      <div className="text-sm text-gray-600 mt-1 italic" style={{ 
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {formatNotesWithLineBreaks(item.notes)}
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center text-gray-700 text-base">{item.quantity}</td>
                  <td className="py-4 px-4 text-right text-gray-700 text-base">{formatCurrency(item.rate)}</td>
                  <td className="py-4 px-4 text-right font-medium text-gray-900 text-sm">
                    {formatCurrency(item.amount)}
                    {(item.taxRate ?? 0) > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Tax: {item.taxRate}%
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-80">
            <div className="space-y-3">
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-semibold">{formatCurrency(invoice.totals.subtotal)}</span>
              </div>
              {invoice.totals.taxAmount > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-semibold">{formatCurrency(invoice.totals.taxAmount)}</span>
                </div>
              )}
              <div className="flex justify-between py-4 border-t-2 border-gray-300 bg-gray-50 px-4 rounded border-l-4" style={{ borderLeftColor: templateStyles.accentColor }}>
                <span className="text-base font-semibold text-gray-900">Total:</span>
                <span className="text-lg font-bold text-black">{formatCurrency(invoice.totals.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        {invoice.paymentDetails && (invoice.paymentDetails.iban || invoice.paymentDetails.bic || invoice.paymentDetails.sortCode || invoice.paymentDetails.accountNumber || invoice.paymentDetails.paymentLink) && (
          <div className="mt-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-600 mb-3">
              Transfer the amount to the account below. Please include invoice number on your reference.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {invoice.paymentDetails.iban && (
                <div>
                  <span className="font-medium text-gray-700">IBAN:</span>
                  <span className="ml-2 text-gray-900 break-all">{invoice.paymentDetails.iban}</span>
                </div>
              )}
              {invoice.paymentDetails.bic && (
                <div>
                  <span className="font-medium text-gray-700">BIC:</span>
                  <span className="ml-2 text-gray-900 break-all">{invoice.paymentDetails.bic}</span>
                </div>
              )}
              {invoice.paymentDetails.sortCode && (
                <div>
                  <span className="font-medium text-gray-700">Sort Code:</span>
                  <span className="ml-2 text-gray-900 break-all">{invoice.paymentDetails.sortCode}</span>
                </div>
              )}
              {invoice.paymentDetails.accountNumber && (
                <div>
                  <span className="font-medium text-gray-700">Account Number:</span>
                  <span className="ml-2 text-gray-900 break-all">{invoice.paymentDetails.accountNumber}</span>
                </div>
              )}
              {invoice.paymentDetails.paymentLink && (
                <div className="md:col-span-2">
                  <span className="font-medium text-gray-700">Payment Link:</span>
                  <a 
                    href={invoice.paymentDetails.paymentLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="ml-2 text-blue-600 hover:text-blue-800 underline break-all"
                  >
                    {invoice.paymentDetails.paymentLink}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderClassicLayout_UNUSED = () => (
    <div className="bg-white">
          {/* Classic Header - Original layout */}
          <div className={`p-6 border-b border-gray-200 border-l-4`} style={{ 
            backgroundColor: customization?.headerBackgroundColor || '#f3f4f6',
            borderLeftColor: templateStyles.accentColor 
          }}>
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
              {/* Company Info */}
          <div className={`flex items-start ${customization?.showLogo === false ? 'items-center' : ''}`}>
            {customization?.showLogo !== false && (uploadedLogo || companySettings.logo) && !logoError ? (
                  <img
                src={uploadedLogo || companySettings.logo}
                    alt={companySettings.companyName}
                className="h-16 w-auto object-contain"
                onError={() => setLogoError(true)}
              />
            ) : customization?.showLogo !== false ? (
              <div className="h-16 w-16 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6" />
                  </div>
            ) : null}
            
            <div className={customization?.showLogo === false ? 'ml-0' : 'ml-4'}>
              <h1 className={`text-xl font-bold ${customization?.showLogo === false ? 'text-right lg:text-left' : ''}`} style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6'),
                fontFamily: customStyles.fontFamily 
              }}>
                    {companySettings.companyName}
                  </h1>
              {customization?.showCompanyAddress !== false && formattedAddress && (
                <div className={`text-sm mt-1 whitespace-pre-line ${customization?.showLogo === false ? 'text-right lg:text-left' : ''}`} style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                  fontFamily: customStyles.fontFamily 
                }}>
                      {formattedAddress}
                    </div>
                  )}
                  {companySettings.phone && (
                <div className={`text-sm mt-1 ${customization?.showLogo === false ? 'text-right lg:text-left' : ''}`} style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                  fontFamily: customStyles.fontFamily 
                }}>
                      <Phone className="w-2.5 h-2.5 inline mr-1" />
                      {companySettings.phone}
                    </div>
                  )}
                  {companySettings.email && (
                <div className={`text-sm mt-1 ${customization?.showLogo === false ? 'text-right lg:text-left' : ''}`} style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                  fontFamily: customStyles.fontFamily 
                }}>
                      <Mail className="w-2.5 h-2.5 inline mr-1" />
                      {companySettings.email}
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Details */}
              <div className="text-right lg:text-left">
                <h2 
              className={`text-2xl font-bold mb-4`}
              style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6'),
                fontFamily: customStyles.fontFamily 
              }}
                >
                  INVOICE
                </h2>
                <div className="space-y-2">
                  <div>
                    <span 
                      className="text-sm"
                  style={{ 
                    color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                    fontFamily: customStyles.fontFamily 
                  }}
                    >
                      Invoice Number:
                    </span>
                <div className="font-semibold" style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6'),
                  fontFamily: customStyles.fontFamily 
                }}>
                  {invoice.invoiceNumber || 'Draft'}
                    </div>
                  </div>
                  <div>
                    <span 
                      className="text-sm"
                  style={{ 
                    color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                    fontFamily: customStyles.fontFamily 
                  }}
                    >
                      Status:
                    </span>
                    <div>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(invoice.status)}`} style={{ fontFamily: customStyles.fontFamily }}>
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

      {/* Classic Content - Original layout continues */}
      <div className="p-4 border-b border-gray-200" style={{ fontFamily: customStyles.fontFamily }}>
        {/* Headers Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-0">
          <div className="pl-2">
            <h3 
              className="text-sm mb-0"
              style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true),
                fontFamily: customStyles.fontFamily 
              }}
                >
                  Bill To:
                </h3>
            <div className="text-gray-900 font-semibold text-sm mt-1" style={{ fontFamily: customStyles.fontFamily }}>
              {invoice.client?.name || 'No client name'}
                    </div>
            {invoice.client?.email && (
              <div className="text-gray-600 text-xs mt-0.5" style={{ fontFamily: customStyles.fontFamily }}>
                {invoice.client.email}
                      </div>
                    )}
            {invoice.client?.phone && (
              <div className="text-gray-600 text-xs mt-0.5" style={{ fontFamily: customStyles.fontFamily }}>
                {invoice.client.phone}
                      </div>
                    )}
            {invoice.client?.address && (
              <div className="flex items-start space-x-2 text-gray-600 text-xs mt-1">
                <MapPin className="w-3 h-3 mt-0.5" />
                <div className="whitespace-pre-line">
                  {formatAddressTwoLine(invoice.client.address)}
                </div>
              </div>
            )}
              </div>

          {/* Invoice Dates Content */}
              <div>
            <div className="space-y-2">
                  <div>
                <div className="font-semibold text-sm" style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6')
                }}>
                  <Calendar className="w-2.5 h-2.5 inline mr-2" />
                      {formatDate(invoice.issueDate)}
                    </div>
                  </div>
                  <div>
                    <label 
                      className="text-sm"
                  style={{ 
                    color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true)
                  }}
                    >
                      Due Date:
                    </label>
                <div className="font-semibold text-sm" style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6')
                }}>
                  <Calendar className="w-2.5 h-2.5 inline mr-2" />
                      {formatDate(invoice.dueDate)}
                    </div>
                  </div>
              {customization?.showPaymentTerms !== false && (
                  <div>
                    <label 
                      className="text-sm"
                  style={{ 
                    color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6', true)
                  }}
                    >
                      Payment Terms:
                    </label>
                <div className="font-semibold text-sm" style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f3f4f6')
                }}>30 days</div>
                    </div>
              )}
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Items */}
      <div className="p-4 border-b border-gray-200" style={{ fontFamily: customStyles.fontFamily }}>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-2 px-2 font-semibold text-gray-900 text-sm">Description</th>
                <th className="text-right py-2 px-2 font-semibold text-gray-900 w-20 text-sm">Qty</th>
                <th className="text-right py-2 px-2 font-semibold text-gray-900 w-24 text-sm">Rate</th>
                <th className="text-right py-2 px-2 font-semibold text-gray-900 w-24 text-sm">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <motion.tr
                      key={item.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-2 px-2">
                    <div className="font-semibold text-gray-900 text-base">
                      {item.description || <span className="italic text-gray-400">No description</span>}
                        </div>
                    {item.notes && (
                      <div className="text-sm text-gray-500 mt-1 italic" style={{ 
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {formatNotesWithLineBreaks(item.notes)}
                      </div>
                    )}
                      </td>
                  <td className="py-2 px-2 text-right text-gray-700 text-base">{item.quantity}</td>
                  <td className="py-2 px-2 text-right text-gray-700 text-base">{formatCurrency(item.rate)}</td>
                  <td className="py-2 px-2 text-right font-semibold text-gray-900 text-base">
                    {formatCurrency(item.amount)}
                    {(item.taxRate ?? 0) > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Tax: {item.taxRate}%
                      </div>
                    )}
                  </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

      {/* Totals */}
      <div className="p-4" style={{ fontFamily: customStyles.fontFamily }}>
            <div className="flex justify-end">
          <div className="w-80">
            <div className="space-y-1">
              <div className="flex justify-between py-1">
                <span className="text-gray-600 text-sm">Subtotal:</span>
                <span className="font-semibold text-gray-900 text-sm">{formatCurrency(invoice.totals.subtotal)}</span>
                </div>
              {invoice.totals.taxAmount > 0 && (
                <div className="flex justify-between py-1">
                  <span className="text-gray-600 text-sm">Tax:</span>
                  <span className="font-semibold text-gray-900 text-sm">{formatCurrency(invoice.totals.taxAmount)}</span>
                  </div>
                )}
                
              <div className="border-t border-gray-300 pt-2">
                  <div className="flex justify-between items-center">
                  <span className="text-xl font-semibold text-gray-900">Total:</span>
                  <div className="bg-gray-100 border-l-4 px-4 py-2 rounded" style={{ borderLeftColor: templateStyles.accentColor }}>
                    <span className="text-2xl font-bold text-black" style={{ fontFamily: customStyles.fontFamily }}>
                      {formatCurrency(invoice.totals.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
                  </div>
                </div>
  );

  const renderCleanProfessionalLayout = () => (
    <div className="bg-white px-4 py-2 pb-6" style={{ minHeight: 'calc(100vh - 1.5rem)' }}>
      {/* Narrow Company Header */}
      <div className={`py-3 px-8 ${customization?.headerBackgroundColor && customization?.headerBackgroundColor !== '#ffffff' && customization?.headerBackgroundColor?.toLowerCase() !== '#fff' ? 'rounded-lg' : ''}`} 
            style={{ 
              backgroundColor: (customization?.headerBackgroundColor && 
                              customization?.headerBackgroundColor !== '#ffffff' && 
                              customization?.headerBackgroundColor?.toLowerCase() !== '#fff' && 
                              customization?.headerBackgroundColor !== 'transparent') 
                              ? customization?.headerBackgroundColor 
                              : 'transparent',
              borderBottom: (customization?.headerBackgroundColor && 
                           customization?.headerBackgroundColor !== '#ffffff' && 
                           customization?.headerBackgroundColor?.toLowerCase() !== '#fff' && 
                           customization?.headerBackgroundColor !== 'transparent') 
                           ? 'none' 
                           : '1px solid #e5e7eb'
            }}>
                  <div className="flex justify-between items-center">
          {/* Left: Logo and Company Info */}
          <div className="flex items-center space-x-4">
            {customization?.showLogo !== false && (uploadedLogo || companySettings.logo) && !logoError ? (
              <img
                src={uploadedLogo || companySettings.logo}
                alt={companySettings.companyName}
                className="h-12 w-auto"
                onError={() => setLogoError(true)}
              />
            ) : customization?.showLogo !== false ? (
              <div className="h-12 w-12 bg-gray-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-gray-500" />
              </div>
            ) : null}
            <div className="text-sm" style={{ fontFamily: customStyles.fontFamily }}>
              <div className="font-semibold" style={{ 
                color: getTextColorForBackground(customization?.headerBackgroundColor || '#f9fafb')
              }}>
                {companySettings.companyName || 'Your Company'}
                    </div>
              {customization?.showCompanyAddress !== false && formattedAddress && (
                <div className="mt-1 whitespace-pre-line" style={{ 
                  color: getTextColorForBackground(customization?.headerBackgroundColor || '#f9fafb', true)
                }}>
                  {formattedAddress}
                  </div>
              )}
                </div>
          </div>

          {/* Right: Contact Info */}
          <div className="text-right text-sm space-y-1" style={{ 
            fontFamily: customStyles.fontFamily,
            color: getTextColorForBackground(customization?.headerBackgroundColor || '#f9fafb', true)
          }}>
            {companySettings.email && (
              <div className="flex items-center justify-end space-x-2">
                <svg style={{width: '14px', height: '14px'}} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                </svg>
                <span>{companySettings.email}</span>
              </div>
            )}
            {companySettings.phone && (
              <div className="flex items-center justify-end space-x-2">
                <svg style={{width: '14px', height: '14px'}} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                </svg>
                <span>{companySettings.phone}</span>
              </div>
            )}
            {companySettings.website && customization?.showWebsite !== false && (
              <div className="flex items-center justify-end space-x-2">
                <svg style={{width: '14px', height: '14px'}} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z"></path>
                </svg>
                {isValidUrl(companySettings.website) ? (
                  <a 
                    href={formatWebsiteUrl(companySettings.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {companySettings.website}
                  </a>
                ) : (
                  <span>{companySettings.website}</span>
                )}
              </div>
            )}
            {companySettings.taxId && customization?.showTaxNumber !== false && (
              <div className="flex items-center justify-end space-x-2">
                <span className="text-sm font-medium">Tax ID:</span>
                <span>{companySettings.taxId}</span>
              </div>
            )}
          </div>
            </div>
          </div>

      {/* Main Content */}
      <div className="p-6">

        {/* Invoice Title and Details Section */}
        <div className="flex justify-between items-start mb-12">
          {/* Left: Recipient */}
                <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3" style={{ fontFamily: customStyles.fontFamily }}>
              Recipient
            </h3>
            <div className="text-gray-900" style={{ fontFamily: customStyles.fontFamily }}>
              <div className="font-semibold text-xl mb-3">{invoice.client?.name || 'No client name'}</div>
              {invoice.client?.address && (
                <div className="text-sm text-gray-600 space-y-1 mb-3 whitespace-pre-line">
                  {formatAddressTwoLine(invoice.client.address)}
                </div>
              )}
              {invoice.client?.email && (
                <div className="flex items-center space-x-2 mt-2 text-sm text-gray-600">
                  <svg style={{width: '14px', height: '14px'}} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                  </svg>
                  <span>{invoice.client.email}</span>
                </div>
              )}
              {invoice.client?.phone && (
                <div className="flex items-center space-x-2 mt-1 text-sm text-gray-600">
                  <svg style={{width: '14px', height: '14px'}} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                  </svg>
                  <span>{invoice.client.phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Invoice Details */}
          <div className="text-right">
            <h1 className="text-4xl font-light mb-4 text-gray-900" style={{ fontFamily: customStyles.fontFamily }}>
              Invoice
            </h1>
            <div className="space-y-2" style={{ fontFamily: customStyles.fontFamily }}>
                <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Invoice No.</div>
                <div className="font-semibold text-xl">{invoice.invoiceNumber}</div>
                  </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Invoice Date</div>
                <div className="text-lg text-gray-900">{formatDate(invoice.issueDate)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Due Date</div>
                <div className="text-lg text-gray-900">{formatDate(invoice.dueDate)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-12">
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-4 font-medium text-gray-500 text-sm" style={{ fontFamily: customStyles.fontFamily }}>
                  Task description
                </th>
                <th className="text-center py-4 font-medium text-gray-500 text-sm w-24" style={{ fontFamily: customStyles.fontFamily }}>
                  Hours
                </th>
                <th className="text-center py-4 font-medium text-gray-500 text-sm w-32" style={{ fontFamily: customStyles.fontFamily }}>
                  Rate
                </th>
                <th className="text-right py-4 font-medium text-gray-500 text-sm w-32" style={{ fontFamily: customStyles.fontFamily }}>
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={item.id} className="border-b border-gray-100">
                  <td className="py-4">
                    <div className="font-medium text-lg text-gray-900" style={{ fontFamily: customStyles.fontFamily }}>
                      {item.description || 'No description'}
                    </div>
                    {item.notes && (
                      <div className="text-sm text-gray-500 mt-1 italic" style={{ 
                        fontFamily: customStyles.fontFamily,
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {formatNotesWithLineBreaks(item.notes)}
                      </div>
                    )}
                  </td>
                  <td className="py-4 text-center text-gray-900 text-lg" style={{ fontFamily: customStyles.fontFamily }}>
                    {item.quantity}
                  </td>
                  <td className="py-4 text-center text-gray-900 text-lg" style={{ fontFamily: customStyles.fontFamily }}>
                    {formatCurrency(item.rate)}
                  </td>
                  <td className="py-4 text-right font-semibold text-gray-900 text-lg" style={{ fontFamily: customStyles.fontFamily }}>
                    {formatCurrency(item.amount)}
                    {(item.taxRate ?? 0) > 0 && (
                      <div className="text-xs text-gray-500 mt-1" style={{ fontFamily: customStyles.fontFamily }}>
                        Tax: {item.taxRate}%
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals Section */}
        <div className="flex justify-end mb-12">
          <div className="w-80 space-y-3" style={{ fontFamily: customStyles.fontFamily }}>
            <div className="flex justify-between py-2 text-base">
              <span className="text-gray-500">Subtotal</span>
              <span className="text-gray-900">{formatCurrency(invoice.totals.subtotal)}</span>
            </div>
            
            {invoice.totals.taxAmount > 0 && (
              <div className="flex justify-between py-2 text-base">
                <span className="text-gray-500">Tax</span>
                <span className="text-gray-900">{formatCurrency(invoice.totals.taxAmount)}</span>
            </div>
          )}

            <div className="flex justify-between py-3 border-t border-gray-200">
              <span className="text-xl font-medium text-gray-900">Total</span>
              <span className="text-xl font-semibold text-gray-900">{formatCurrency(invoice.totals.total)}</span>
            </div>
              </div>
        </div>

        {/* Notes */}
        {invoice.notes && customization?.showNotes !== false && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: customStyles.fontFamily }}>Notes</h3>
            <div className="text-sm text-gray-600" style={{ fontFamily: customStyles.fontFamily }}>
              {invoice.notes.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Details */}
        {invoice.paymentDetails && (invoice.paymentDetails.iban || invoice.paymentDetails.bic || invoice.paymentDetails.sortCode || invoice.paymentDetails.accountNumber || invoice.paymentDetails.paymentLink) && (
          <div className="mt-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-600 mb-4 text-left" style={{ fontFamily: customStyles.fontFamily }}>
              Transfer the amount to the account below. Please include invoice number on your reference.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {invoice.paymentDetails.iban && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">IBAN</div>
                  <div className="font-semibold text-gray-900 break-all" style={{ fontFamily: customStyles.fontFamily }}>{invoice.paymentDetails.iban}</div>
                </div>
              )}
              {invoice.paymentDetails.bic && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">BIC</div>
                  <div className="font-semibold text-gray-900 break-all" style={{ fontFamily: customStyles.fontFamily }}>{invoice.paymentDetails.bic}</div>
                </div>
              )}
              {invoice.paymentDetails.sortCode && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Sort Code</div>
                  <div className="font-semibold text-gray-900 break-all" style={{ fontFamily: customStyles.fontFamily }}>{invoice.paymentDetails.sortCode}</div>
                </div>
              )}
              {invoice.paymentDetails.accountNumber && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Account Number</div>
                  <div className="font-semibold text-gray-900 break-all" style={{ fontFamily: customStyles.fontFamily }}>{invoice.paymentDetails.accountNumber}</div>
                </div>
              )}
              {invoice.paymentDetails.paymentLink && (
                <div className="md:col-span-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Payment Link</div>
                  <a 
                    href={invoice.paymentDetails.paymentLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold break-all"
                    style={{ fontFamily: customStyles.fontFamily }}
                  >
                    {invoice.paymentDetails.paymentLink}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const previewScale = 'scale-100';
  const previewWidth = 'max-w-4xl mx-auto';
  
  // A4 dimensions in pixels (at 96 DPI)
  const A4_WIDTH = 794; // 210mm at 96 DPI
  const A4_HEIGHT = 1123; // 297mm at 96 DPI

  return (
    <div className="h-full bg-card p-3 sm:p-4 lg:p-6 overflow-y-auto rounded-t-lg">
      {/* Load Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Open+Sans:wght@400;500;600;700&family=Lato:wght@400;700&family=Montserrat:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Print styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            .no-break {
              page-break-inside: avoid;
            }
          }
        `
      }} />

      {/* Preview Controls */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-5 md:flex-row md:items-center md:justify-between">
        {/* Template Selector */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-4">
          <h3 className="w-full text-base font-semibold text-foreground sm:w-auto sm:text-lg">Live Preview</h3>

          <div className="template-selector flex min-w-0 items-center gap-2">
            <button
              onClick={() => {
                lastDirectionRef.current = 'left';
                setAnimationDirection('left');
                setAnimationKey(prev => prev + 1);
                onTemplateChange?.('prev');
                // Remove focus after click to prevent outline from staying
                (document.activeElement as HTMLElement)?.blur();
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-border focus:outline-none transition-colors duration-200"
              style={{ outline: 'none', boxShadow: 'none' }}
              title="Previous Template"
            >
              <ChevronLeft className="w-3 h-3" />
            </button>

            <div className="template-name relative flex h-9 w-[11rem] items-center justify-center overflow-hidden rounded-full border border-border px-4 py-2">
              <AnimatePresence mode="wait">
                <motion.span
                  key={`${template}-${animationKey}`}
                  initial={{ 
                    x: animationDirection === 'right' ? -180 : 180,
                    opacity: 0 
                  }}
                  animate={{ 
                    x: 0,
                    opacity: 1 
                  }}
                  exit={{ 
                    x: animationDirection === 'right' ? 180 : -180,
                    opacity: 0 
                  }}
                  transition={{ 
                    duration: 0.15,
                    ease: 'easeOut'
                  }}
                  className="absolute inset-0 flex items-center justify-center text-sm font-semibold capitalize text-foreground"
                >
                  {availableTemplates[template]?.name || template}
                </motion.span>
              </AnimatePresence>
            </div>

            <button
              onClick={() => {
                lastDirectionRef.current = 'right';
                setAnimationDirection('right');
                setAnimationKey(prev => prev + 1);
                onTemplateChange?.('next');
                // Remove focus after click to prevent outline from staying
                (document.activeElement as HTMLElement)?.blur();
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-border focus:outline-none transition-colors duration-200"
              style={{ outline: 'none', boxShadow: 'none' }}
              title="Next Template"
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          {/* Right side - Download PDF Button */}
          {onDownloadPDF && (
            <div className="relative hidden md:block" data-download-button>
              <button
                onClick={() => {
                  if (formValidationState) {
                    onDownloadPDF();
                  } else {
                    // Trigger existing validation system
                    if (setValidationErrors) {
                      // Run the same validation logic as in InvoiceDesigner
                      const errors: any = {};
                      
                      // Business validation
                      if (!formData.businessName || !formData.businessName.trim()) {
                        errors.businessName = 'Business name is required';
                      }
                      if (!formData.businessAddress || !formData.businessAddress.trim()) {
                        errors.businessAddress = 'Business address is required';
                      }
                      if (!formData.businessCity || !formData.businessCity.trim()) {
                        errors.businessCity = 'Business city is required';
                      }
                      if (!formData.businessState || !formData.businessState.trim()) {
                        errors.businessState = 'Business state is required';
                      }
                      if (!formData.businessZipCode || !formData.businessZipCode.trim()) {
                        errors.businessZipCode = 'Business postal code is required';
                      }
                      
                      // Customer validation
                      if (!formData.customerName || !formData.customerName.trim()) {
                        errors.customerName = 'Customer name is required';
                      }
                      if (!formData.customerAddress || !formData.customerAddress.trim()) {
                        errors.customerAddress = 'Customer address is required';
                      }
                      if (!formData.customerCity || !formData.customerCity.trim()) {
                        errors.customerCity = 'Customer city is required';
                      }
                      if (!formData.customerState || !formData.customerState.trim()) {
                        errors.customerState = 'Customer state is required';
                      }
                      if (!formData.customerZipCode || !formData.customerZipCode.trim()) {
                        errors.customerZipCode = 'Customer postal code is required';
                      }
                      
                      // Due date validation
                      if (!formData.dueDate || !formData.dueDate.trim()) {
                        errors.dueDate = 'Due date is required';
                      }
                      
                      // Line items validation
                      if (formData.lineItems.length === 0) {
                        errors.lineItems = 'At least one line item is required';
                      } else {
                        const emptyItems = formData.lineItems.filter((item: any) => !item.description || !item.description.trim());
                        if (emptyItems.length > 0) {
                          errors.lineItems = 'All line items must have descriptions';
                        }
                        
                        const zeroQuantityItems = formData.lineItems.filter((item: any) => item.quantity <= 0);
                        if (zeroQuantityItems.length > 0) {
                          errors.lineItems = 'All items must have valid quantities greater than zero';
                        }
                        
                        const zeroRateItems = formData.lineItems.filter((item: any) => item.rate <= 0);
                        if (zeroRateItems.length > 0) {
                          errors.lineItems = 'All items must have valid unit prices greater than zero';
                        }
                      }
                      
                      // Email validation
                      if (!formData.businessEmail || !formData.businessEmail.trim()) {
                        errors.businessEmail = 'Business email is required';
                      } else if (!isMinimalInvoiceEmailValid(formData.businessEmail)) {
                        errors.businessEmail = 'Email Invalid';
                      }

                      if (!formData.customerEmail || !formData.customerEmail.trim()) {
                        errors.customerEmail = 'Client email is required';
                      } else if (!isMinimalInvoiceEmailValid(formData.customerEmail)) {
                        errors.customerEmail = 'Email Invalid';
                      }
                      
                      // Website validation (optional - only validate format if provided)
                      if (formData.businessWebsite && formData.businessWebsite.trim() && 
                          !formData.businessWebsite.startsWith('http://') && !formData.businessWebsite.startsWith('https://')) {
                        errors.businessWebsite = 'Please enter a valid website URL (must start with http:// or https://)';
                      }
                      
                      setValidationErrors(errors);
                    }
                  }
                }}
                disabled={isGeneratingPDF}
                className={`group flex h-9 w-[11rem] items-center justify-center space-x-2 rounded-[20px] border px-4 py-2 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-0 ${
                  formValidationState
                    ? (isDark ? 'border-primary/50 text-primary' : 'border-primary/50')
                    : isDark
                      ? 'border-gray-600/30 text-gray-400 invalid'
                      : 'border-gray-300 text-gray-500 invalid'
                }`}
                onMouseEnter={(e) => {
                  if (!isGeneratingPDF) {
                    if (formValidationState) {
                      if (isDark) {
                        e.currentTarget.style.borderColor = 'rgba(237, 255, 0, 0.7)';
                        e.currentTarget.style.background = 'rgba(167, 238, 67, 0.2)';
                        e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 4px rgba(0, 0, 0, 0.15)';
                      } else {
                        e.currentTarget.style.borderColor = 'rgba(237, 255, 0, 0.7)';
                        e.currentTarget.style.background = '#edff00';
                        e.currentTarget.style.color = '#1a1a1a';
                        e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 4px rgba(0, 0, 0, 0.15)';
                      }
                    } else {
                      if (isDark) {
                        e.currentTarget.style.borderColor = '#4b5563';
                        e.currentTarget.style.background = '#374151';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.opacity = '0.8';
                      } else {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isGeneratingPDF) {
                    if (formValidationState) {
                      if (isDark) {
                        e.currentTarget.style.borderColor = 'rgba(237, 255, 0, 0.5)';
                        e.currentTarget.style.background = 'rgba(167, 238, 67, 0.12)';
                        e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)';
                      } else {
                        e.currentTarget.style.borderColor = 'rgba(237, 255, 0, 0.5)';
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.color = '#1a1a1a';
                        e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)';
                      }
                    } else {
                      if (isDark) {
                        e.currentTarget.style.borderColor = '#374151';
                        e.currentTarget.style.background = '#1f2937';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.opacity = '0.7';
                      } else {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }
                  }
                }}
                style={{
                  background: formValidationState 
                    ? (isDark ? 'rgba(167, 238, 67, 0.12)' : '#ffffff')
                    : (isDark ? '#1f2937' : '#f3f4f6'),
                  color: formValidationState 
                    ? (isDark ? '#edff00' : '#1a1a1a')
                    : (isDark ? '#9ca3af' : '#6b7280'),
                  backdropFilter: isDark ? (formValidationState ? 'blur(20px)' : 'blur(10px)') : 'none',
                  WebkitBackdropFilter: isDark ? (formValidationState ? 'blur(20px)' : 'blur(10px)') : 'none',
                  boxShadow: formValidationState 
                    ? (isDark ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)')
                    : (isDark ? 'inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'),
                  height: '36px',
                  width: '176px',
                  flexShrink: 0,
                  outline: 'none',
                  cursor: !isGeneratingPDF ? 'pointer' : 'not-allowed'
                }}
                title={
                  isGeneratingPDF 
                    ? 'Generating PDF...' 
                    : !formValidationState 
                      ? 'Complete all required fields to download PDF'
                      : 'Download PDF'
                }
              >
                <div className="w-5 h-5 rounded-full flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    {isGeneratingPDF ? (
                      <motion.div
                        key="loading"
                        initial={{ opacity: 0, rotate: -45 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        exit={{ opacity: 0, rotate: 45 }}
                        transition={{ duration: 0.1 }}
                      >
                        <Loader2 
                          className={`w-4 h-4 animate-spin transition-colors ${formValidationState ? (isDark ? 'text-primary' : 'text-[#1a1a1a]') : (isDark ? 'text-gray-400' : 'text-gray-500')}`}
                        />
                      </motion.div>
                    ) : formValidationState ? (
                      <motion.div
                        key="download"
                        initial={{ opacity: 0, rotate: -45 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        exit={{ opacity: 0, rotate: 45 }}
                        transition={{ duration: 0.1 }}
                      >
                        <Download 
                          className={`w-4 h-4 transition-colors ${formValidationState ? (isDark ? 'text-primary' : 'text-[#1a1a1a]') : (isDark ? 'text-gray-400' : 'text-gray-500')}`}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="invalid"
                        initial={{ opacity: 0, rotate: -45 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        exit={{ opacity: 0, rotate: 45 }}
                        transition={{ duration: 0.1 }}
                      >
                        <X 
                          className={`w-4 h-4 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <span 
                  className="text-sm"
                  style={{
                    color: formValidationState 
                      ? (isDark ? '#edff00' : '#1a1a1a')
                      : (isDark ? '#9ca3af' : '#6b7280')
                  }}
                >
                  {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
                </span>
              </button>
            </div>
          )}
        </div>

      </div>

      {/* Editor Toolbar */}
      {customizationStyles && updateCustomStyle && (
        <div className="editor-toolbar relative mb-4 grid grid-cols-2 gap-2 overflow-visible rounded-2xl border border-border bg-secondary/30 px-2 py-2 md:flex md:items-center md:justify-between md:rounded-full md:px-4">
          {/* Left side - Design tools */}
          <div className="contents md:flex md:min-w-0 md:items-center md:gap-2 md:pb-0">
            {/* Primary Color */}
            <ColorPicker
              value={customizationStyles.primaryColor || '#3B82F6'}
              onChange={(color) => updateCustomStyle('primaryColor', color)}
              title="Primary Color"
            />

            {/* Header Background Color */}
            <ColorPicker
              value={customizationStyles.headerBackgroundColor || '#FFFFFF'}
              onChange={(color) => updateCustomStyle('headerBackgroundColor', color)}
              title="Header Background Color"
            />

            <div className="mx-2 hidden h-6 w-px bg-border md:block"></div>

            {/* Currency Selector */}
            {currency && onCurrencyChange && showCurrencyDropdown !== undefined && setShowCurrencyDropdown && getCurrencySymbol && (
              <div className="relative w-full md:w-auto" data-currency-dropdown>
                <button
                  onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                  className={`flex w-full items-center justify-between space-x-2 rounded-full border border-transparent bg-card px-2 py-2 transition-all duration-200 group hover:bg-muted focus:outline-none focus:ring-0 md:w-auto md:px-3 ${isDark ? 'hover:border-[#edff00]/60' : 'hover:border-[#4c730f]/60'}`}
                  style={{ outline: 'none', boxShadow: 'none' }}
                  title={`Currency: ${currency} (${getCurrencySymbol(currency)})`}
                >
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">{getCurrencySymbol(currency)}</span>
                  </div>
                  <span className="text-sm">{currency}</span>
                  <ChevronDown className={`w-4 h-4 transition-colors transition-transform duration-300 ease-out ${isDark ? 'text-[#edff00] group-hover:text-[#d4e600]' : 'text-slate-600 group-hover:text-slate-500'} ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {showCurrencyDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-[70] min-w-[200px] max-w-[300px]"
                    >
                      {/* Search Input */}
                      <div className="p-3 border-b border-border">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input
                            type="text"
                            placeholder="Search currencies..."
                            value={currencySearchQuery}
                            onChange={(e) => setCurrencySearchQuery(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 text-sm bg-transparent border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            autoFocus
                          />
                        </div>
                      </div>
                      
                      {/* Currency List */}
                      <div className="max-h-[200px] overflow-y-auto">
                        {getFilteredCurrencies().length > 0 ? (
                          getFilteredCurrencies().map((curr) => (
                            <button
                              key={curr.code}
                              onClick={() => {
                                onCurrencyChange(curr.code);
                                setShowCurrencyDropdown(false);
                                setCurrencySearchQuery('');
                              }}
                              className={`w-full px-3 py-2 text-sm text-left transition-colors hover:bg-muted ${
                                currency === curr.code ? 'bg-primary text-primary-foreground' : 'text-foreground'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>{curr.code}</span>
                                <span className="text-muted-foreground">{curr.symbol}</span>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No currencies found
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            <div className="mx-2 hidden h-6 w-px bg-border md:block"></div>

            {/* Font Family */}
            <div className="relative w-full md:w-auto font-dropdown">
              <button
                onClick={() => setShowFontDropdown(!showFontDropdown)}
                className={`flex w-full items-center justify-between space-x-2 rounded-full border border-transparent bg-card px-2 py-2 transition-all duration-200 group hover:bg-muted focus:outline-none focus:ring-0 md:w-auto md:px-3 ${isDark ? 'hover:border-[#edff00]/60' : 'hover:border-[#4c730f]/60'}`}
                style={{ outline: 'none', boxShadow: 'none' }}
                title="Font Family"
              >
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">Aa</span>
                </div>
                <span className="text-sm">{customizationStyles.fontFamily || 'Inter'}</span>
                <ChevronDown className={`w-4 h-4 transition-colors transition-transform duration-300 ease-out ${isDark ? 'text-[#edff00] group-hover:text-[#d4e600]' : 'text-slate-600 group-hover:text-slate-500'} ${showFontDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {showFontDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-[70] min-w-[140px]"
                  >
                    {['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Playfair Display'].map((font) => (
                      <button
                        key={font}
                        onClick={() => {
                          updateCustomStyle('fontFamily', font);
                          setShowFontDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-sm text-left transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          (customizationStyles.fontFamily || 'Inter') === font ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                        }`}
                        style={{ 
                          backgroundColor: (customizationStyles.fontFamily || 'Inter') === font ? undefined : 'var(--input)',
                          fontFamily: font
                        }}
                      >
                        {font}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right side - Display options dropdown */}
          <div className="relative display-options-dropdown w-full md:w-auto shrink-0 self-start md:self-auto">
            <button
              onClick={() => setShowDisplayOptionsDropdown?.(!showDisplayOptionsDropdown)}
              className={`flex w-full items-center justify-between space-x-2 rounded-full border border-transparent bg-card px-2 py-2 transition-all duration-200 group hover:bg-muted focus:outline-none focus:ring-0 md:w-auto md:px-3 ${isDark ? 'hover:border-[#edff00]/60' : 'hover:border-[#4c730f]/60'}`}
              style={{ outline: 'none', boxShadow: 'none' }}
              title="Display Options"
            >
              <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                <MoreHorizontal className={`w-4 h-4 transition-colors ${isDark ? 'text-[#edff00] group-hover:text-[#d4e600]' : 'text-slate-600 group-hover:text-slate-500'}`} />
              </div>
              <span className="text-sm">Options</span>
              <ChevronDown className={`w-4 h-4 transition-colors transition-transform duration-300 ease-out ${isDark ? 'text-[#edff00] group-hover:text-[#d4e600]' : 'text-slate-600 group-hover:text-slate-500'} ${showDisplayOptionsDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Display Options Dropdown */}
            <AnimatePresence>
            {showDisplayOptionsDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
                className="absolute left-0 top-full mt-1 w-[min(22rem,calc(100vw-1rem))] bg-card border border-border rounded-lg shadow-lg z-50 md:left-auto md:right-0 md:w-56"
              >
                <div className="p-3 space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground mb-2">Display Options</div>
                  
                  <label className={`flex items-center space-x-3 ${!(uploadedLogo || companySettings.logo) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={customizationStyles.showLogo !== false}
                      onChange={(e) => updateCustomStyle('showLogo', e.target.checked)}
                      disabled={!(uploadedLogo || companySettings.logo)}
                      className="w-4 h-4 text-lime-500 border-border rounded focus:ring-lime-500 focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ accentColor: '#edff00' }}
                    />
                    <span className={`text-sm ${!(uploadedLogo || companySettings.logo) ? 'text-muted-foreground' : 'text-foreground'}`}>
                      Show Company Logo
                      {!(uploadedLogo || companySettings.logo) && (
                        <span className="text-xs text-muted-foreground ml-1">(No logo uploaded)</span>
                      )}
                    </span>
                  </label>
                  
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customizationStyles.showWebsite !== false}
                      onChange={(e) => updateCustomStyle('showWebsite', e.target.checked)}
                      className="w-4 h-4 text-lime-500 border-border rounded focus:ring-lime-500 focus:ring-2"
                      style={{ accentColor: '#edff00' }}
                    />
                    <span className="text-sm text-foreground">Show Website</span>
                  </label>
                  
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customizationStyles.showTaxNumber !== false}
                      onChange={(e) => updateCustomStyle('showTaxNumber', e.target.checked)}
                      className="w-4 h-4 text-lime-500 border-border rounded focus:ring-lime-500 focus:ring-2"
                      style={{ accentColor: '#edff00' }}
                    />
                    <span className="text-sm text-foreground">Show Tax Number</span>
                  </label>
                  
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customizationStyles.showPaymentTerms !== false}
                      onChange={(e) => updateCustomStyle('showPaymentTerms', e.target.checked)}
                      className="w-4 h-4 text-lime-500 border-border rounded focus:ring-lime-500 focus:ring-2"
                      style={{ accentColor: '#edff00' }}
                    />
                    <span className="text-sm text-foreground">Show Payment Terms</span>
                  </label>
                  
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customizationStyles.showNotes !== false}
                      onChange={(e) => updateCustomStyle('showNotes', e.target.checked)}
                      className="w-4 h-4 text-lime-500 border-border rounded focus:ring-lime-500 focus:ring-2"
                      style={{ accentColor: '#edff00' }}
                    />
                    <span className="text-sm text-foreground">Show Notes Section</span>
                  </label>
                </div>
              </motion.div>
            )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Invoice Preview */}
      <motion.div
        layout
        className={`${previewScale} transition-transform duration-300 flex justify-center`}
      >
        <div 
          id="invoice-preview"
          className={`bg-white text-black overflow-y-auto max-h-[calc(100vh-200px)] ${templateStyles.shadow}`}
            style={{ 
            fontFamily: customStyles.fontFamily || '"Inter", sans-serif',
            width: `${A4_WIDTH}px`,
            maxWidth: '100%'
          }}
        >
          {renderInvoiceLayout()}
        </div>
      </motion.div>
    </div>
  );
};
